import { validFilterKeys, months } from "./constants";
import { jobCategoryItems } from "./searchFacetItems";
import { getFeatureFlags } from "./featureFlags";

/**
 * Objects with unknown keys and values.
 */
interface IMetadataObj {
    [key: string]: any;
}

/**
 * Convert a word to proper Case.
 */
const titleWordCase = (txt: string): string => `${txt.charAt(0).toUpperCase()}${txt.substr(1).toLowerCase()}`;

/**
 * Convert a sentence to proper Case.
 */
const titleCase = (txt: string): string => txt.split(" ").map(titleWordCase).join(" ");

/**
 * Converts a diacritic to normalized character plus accent. Then removes the accent.
 */
const normalizeAccents = (txt: string): string => txt.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Extract the prop from the provided string based on prefix.
 */
const extractProp = (txt: string, pre: string): string => {
    const prefix = `${pre}-`;
    const suffix = "-jobs";
    const separator = "+";
    // get the end of the path and remove the query string
    const path = txt.split("/").pop().split("?").shift();
    return path.split(separator)
        .filter((el: string) => el.startsWith(prefix))
        .map(decodeURIComponent)
        .map((el: string) => el.replace(prefix, "")
            .replace(suffix, "")
            .replace(/-/g, " ")
            .split(",")
            .join(", "))
        .shift() ?? "";
};

/**
 * Convert number to string and add a comma for thousands and millions.
 */
const formatNumberComma = (value: number) => new Intl.NumberFormat("en-US").format(value);

/**
 * Retrieves the company from the provided string.
 * Assumes you are passing the canonical path.
 */
const getCompanyFromPath = (txt: string): string => extractProp(txt, "c")
    .split(" ")
    .map(titleCase)
    // Converts a diacritic to normalized character plus accent. Then removes the accent.
    .map(normalizeAccents)
    .join(" ")
    .replace(/\s\s+/g, " ");

/**
 * Retrieves the location from the provided string.
 * Assumes you are passing the canonical path.
 */
const getLocationFromPath = (txt: string): string => extractProp(txt, "l")
    .split(" ")
    .map(titleCase)
    .map(normalizeAccents)
    // Convert State characters to upper case
    .map((item, index, array) => (index === array.length - 1 ? item.toUpperCase() : item))
    .join(" ")
    .replace(/\s\s+/g, " ");

/**
 * Returns the formatted portion of the location for SEO.
 */
const getCanonicalWhere = (splitPath: string[]): string[] => {
    const locationIndex = splitPath.indexOf("location");
    const location = locationIndex > 0 ? ["location", splitPath[locationIndex + 1]] : [];
    if (location.length > 0 && location[1] === "flexible-remote") {
        location[1] = "remote-flexible";
    }
    return location;
};

/**
 * Returns the formatted category for SEO.
 */
const getCanonicalCategory = (splitPath: string[]): string[] => {
    const categoryIndex = splitPath.indexOf("category");
    let category = [];
    if (categoryIndex >= 0) {
        category = ["category", splitPath[categoryIndex + 1]];
        // If there is more than one category then choose the first category.
        if (category[1].toLowerCase().includes("%2c")) {
            const splitCategories = category[1].toLowerCase().split("%2c").filter(Boolean);
            category = ["category", splitCategories[0]];
        }
    }
    return category;
};

/**
 * gets the canonical priority "what". Order of priority: keyword > company > category
 */
const getCanonicalWhat = (splitPath: string[]): string[] => {
    // Companies
    const companyIndex = splitPath.indexOf("company");
    const company = companyIndex > 0 ? ["company", splitPath[companyIndex + 1]] : [];
    // Categories
    const category = getCanonicalCategory(splitPath);
    // Keywords
    const keywordIndex = splitPath.indexOf("keyword");
    const keyword = keywordIndex > 0 ? ["keyword", slugify(splitPath[keywordIndex + 1])] : [];
    // We need to choose only one facet between keyword, company or category
    // order of priority: keyword > company > category
    let priorityFacet = [];
    if (keyword.length) {
        priorityFacet = keyword;
    } else if (company.length) {
        priorityFacet = company;
    } else if (category.length) {
        priorityFacet = category;
    }
    return priorityFacet;
};

/**
 * generate the canonical URL.
 */
const getCanonicalUrl = (asPath: string = "/"): string => {
    const preface = "https://www.themuse.com";
    const splitPath = asPath.split("?")[0].split("/").filter(Boolean);
    const route = "hiring";

    const location = getCanonicalWhere(splitPath);
    const priorityFacet = getCanonicalWhat(splitPath);

    // If the path contains facets but NONE of the acceptable ones (keyword, location, company, category)
    // We return null as there should be no canonical URL
    if (splitPath.length > 1 && !(location.length || priorityFacet.length)) {
        return null;
    }
    // facets order: location always comes first when it exists
    const canonical = [
        preface,
        route,
        ...location,
        ...priorityFacet,
    ].filter(Boolean).join("/");

    return `${canonical}/`;
};

/**
 * Get the label portion of a GroupListItem.
 */
const getFilterLabel = (filterString: string) => {
    const filteredCategory = jobCategoryItems?.filter((category) => category.value === filterString);
    return filteredCategory[0]?.label || "";
};

/**
 * Selects the right SEO what
 * Order of priority: keyword > company > category
 * @param keyword string
 * @param company string
 * @param category string
 * @returns string
 */
const getSeoWhat = (keyword: string, company: string, category: string): string => {
    if (keyword) {
        return `${keyword} `;
    }
    if (company) {
        return `${company} `;
    }
    if (category) {
        return `${getFilterLabel(category)} `;
    }
    return "";
};

/**
 * returns the formatted where for seo title and description
 * @param location: string
 * @param total: count
 * @returns string
 */
const getSeoWhere = (location: string): string => {
    if (location) {
        return isRemoteCheck(location) ? "Remote-Friendly " : ` in ${location}`;
    }
    return "";
};

/**
 * Return singular or plural job(s).
 */
const getSeoNoun = (total: number, isRemote: boolean, what: string): string => {
    if (what || isRemote) {
        return total === 1 ? "job" : "jobs";
    }
    return total === 1 ? "Job" : "Jobs";
};

/**
 * Returns an object containing a string describing search results.
 * @param location: string
 * @param keyword: string
 * @param company: string
 * @param category: string
 * @param total: number
 * @returns object
 */
const getSearchSeoTitle = (
    location: string,
    keyword: string,
    company: string,
    category: string,
    total: number
): { totalString: string, suffix: string } => {
    const isRemote = /remote/i.test(location);
    const totalString = total >= 10000 ? "10,000+ " : `${formatNumberComma(total)} `;
    const what = getSeoWhat(keyword, company, category);
    const where = getSeoWhere(location);
    const noun = getSeoNoun(total, isRemote, what);
    const suffix = isRemote ? `${where}${what}${noun}` : `${what}${noun}${where}`;
    return { totalString, suffix };
};

/**
 * Returns a string describing search results.
 * @param location string
 * @param keyword: string
 * @param company: string
 * @param category: string
 * @param total: number
 * @returns object
 */
const getSearchSeoDescription = (
    location: string,
    keyword: string,
    company: string,
    category: string,
    total: number
): string => {
    const isRemote = /remote/i.test(location);
    const what = getSeoWhat(keyword, company, category);
    const noun = getSeoNoun(total, isRemote, what);
    const where = getSeoWhere(location);
    const prefix = isRemote
        ? `${where}${what}${noun}`
        : `${what}${noun}${where}`;
    return `${prefix} hiring now on The Muse.`;
};

/**
 * Is this one of the 3 named landing pages.
 * Basically we should index any page that is one of:
 * * company filter with no other filters
 * * location filter with no other filters
 * * category filter with no other filters
 */
const isLandingPage = (txt: string = "") => {
    // begins with /search
    // followed by company or location or category
    // followed by a slash
    // followed by a random string
    // followed by at most 1 slash but nothing else

    const regex = new RegExp(/^\/hiring\/(company|location|category)\/[a-zA-Z0-9-]+.\/?$/);
    return regex.test(txt.split("?")[0]);
};

const parsePath = (urlPath: string) => {
    const regex = /^\/((search|hiring)\/)?/;

    return urlPath.replace(regex, "")
        .split("/").reduce((values, pathPart, index, shortenedPath): IMetadataObj => {
            const localValues = values;

            if (index % 2) {
                localValues[shortenedPath[index - 1]] = pathPart;
            } else {
                localValues[pathPart] = "";
            }

            return localValues;
        }, {} as IMetadataObj);
};

/**
 * Rename the query params to the correct new values
 */
const filterValidQueryParams = (paths: IMetadataObj) => {
    const flags = getFeatureFlags();
    return Object.keys(paths)
        .reduce((values, currentValue): IMetadataObj => {
            const localValues = values;
            const validParams = Object.values(validFilterKeys).concat(Object.values(flags));

            if (validParams.includes(currentValue)) {
                const param = validParams[validParams.indexOf(currentValue)];
                localValues[param] = paths[currentValue];
            }

            return localValues;
        }, {} as IMetadataObj);
};

/**
 *  Rename the object names to query param names
 */
const objToQueryParams = (obj: IMetadataObj): string => {
    const mappedObj = Object.keys(obj).reduce((acc, key) => ({
        ...acc,
        ...{ [validFilterKeys[key] || key]: encodeURIComponent(obj[key]) }
    }), {} as IMetadataObj);

    const queryParamStr = Object.keys(mappedObj)
        .filter((key) => {
            if (mappedObj[key] && mappedObj[key].length > 0) {
                return key;
            }
            return null;
        })
        .map((key: string) => `${key}/${mappedObj[key] || ""}`)
        .join("/");
    return queryParamStr;
};

/**
 * Convert the query string into an object
 */
const queryStringToObj = (searchString: string): IMetadataObj => {
    const pairs: string[] = searchString.split("&");
    const result: IMetadataObj = {};
    pairs.forEach((pair) => {
        const pairSplit: string[] = pair.split("=");
        let pairSplitValue: string;
        try {
            pairSplitValue = decodeURIComponent(pairSplit[1] ?? "");
        } catch (error) {
            pairSplitValue = pairSplit[1] ?? "";
        }
        if (pairSplit[0] in result) {
            // check for additional values and add it to the result[key]
            const splitValues = result[pairSplit[0]].split(",");
            splitValues.push(pairSplitValue);
            result[pairSplit[0]] = splitValues.join(",");
        } else {
            result[pairSplit[0]] = pairSplitValue;
        }
    });

    return result;
};

/**
 * Convert an object to query string removing nulls.
 */
const objToQueryString = (obj: IMetadataObj): string => Object.keys(obj)
    .map((key: string) => `${key}=${obj[key] || ""}`)
    .join("&");
/**
 * Remove all contiguous whiteSpace,
 * * ' ' spaces.
 * * \n linefeed (LF or 0x0A (10) in ASCII).
 * * \r carriage return (CR or 0x0D (13) in ASCII).
 * * \t horizontal tab (HT or 0x09 (9) in ASCII).
 * * \v vertical tab (VT or 0x0B (11) in ASCII).
 * * \f form feed (FF or 0x0C (12) in ASCII).
 */
const collapseWhiteSpace = (content?: string | null): string => (content || "")
    .trim()
    .replace(/[ \n\r\t\v\f]+/gm, " "); // replace non-printing characters

/**
 * Convert a string to a slug.
 * * Decode URL encoded strings.
 * * Remove all white space with collapseWhiteSpace.
 * * Convert all diacritic to generics.
 * * Replace not alpha-numerics with dashes.
 * * Collapse all dashes to one dash.
 */
const slugify = (text?: string | null): string => decodeURIComponent(collapseWhiteSpace(text || ""))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/gmi, "-")
    .replace(/-+/g, "-");

/**
 * Replace slugs with spaces and Title case each word
 */
const unSlugify = (text: string = ""): string => titleCase(text
    .split("-")
    .join(" "));

/**
 * Formats a date in correct format for job card
 */
const formatDate = (date: Date) => {
    const month = months[date.getMonth()];
    const day = date.getUTCDate();

    return `${month} ${day}`;
};

/**
 * Checks to see if the provided location should be considered 'remote'
 */
const isRemoteCheck = (location) => (location ? ["Remote / Flexible", "Flexible / Remote"]
    .map((remoteString) => remoteString.toLowerCase()).includes(location.toLowerCase()) : false);

/**
 * Returns a string normalized location display.
 * @param locationName string
 * @returns string
 */
const normalizeLocationName = (locationName: string): string => {
    if (slugify(locationName) === "flexible-remote") {
        return ("Remote / Flexible");
    }
    return locationName;
};

/**
 * Generates a random 36 character Alpha Numeric string.
 */
const generateRandomString = () => Math.random().toString(36).substring(2);

/**
 * Replace all non alpha characters with underscores leaving . and + in place.
 */
const camelCaseToUnderscore = (string: string) => string.replace(
    /(\.?([A-Z]+))/g,
    (match) => `_${match.toLowerCase()}`
);

/**
 * Returns true if the param is truthy or the string true.
 */
const isTruthy = (string: string) => /^(true|1)$/.test(string.toLowerCase());

export {
    collapseWhiteSpace,
    formatNumberComma,
    generateRandomString,
    getCompanyFromPath,
    getSeoNoun,
    getLocationFromPath,
    getSearchSeoTitle,
    getSearchSeoDescription,
    objToQueryString,
    queryStringToObj,
    getCanonicalUrl,
    isLandingPage,
    titleCase,
    parsePath,
    filterValidQueryParams,
    objToQueryParams,
    slugify,
    unSlugify,
    formatDate,
    normalizeLocationName,
    camelCaseToUnderscore,
    isTruthy
};

export type { IMetadataObj };
